"use client"

import { useState, useEffect } from "react"
import { connect } from "react-redux"
import { motion } from "framer-motion"
import { Plus, Search, RefreshCw } from "lucide-react"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Skeleton } from "../../components/ui/skeleton"
import { useToast } from "../../components/ui/use-toast"
import SuppliersTable from "./components/SuppliersTable"
import SupplierModal from "./components/SupplierModal"
import SupplierFilterBar from "./components/SupplierFilterBar"
import { getSuppliers } from "../../../ApiCalls/mantisFlowsSuppliers"
import { selectAuthState } from "../../../store/selectors/authSelectors"

function SuppliersManagement({ token, mantisBusiness, user, embedded = false }) {
  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [modalMode, setModalMode] = useState("view"); // ver, crear, editar
  const [filters, setFilters] = useState({
    favorite: false,
    country: null,
    minRating: 0
  });

  const {toast} = useToast();

  // Cargar proveedores al iniciar
  useEffect(() => {
    loadSuppliers();

    // Establecer listener para evento de carga
    window.addEventListener("suppliersLoading", handleLoadingEvent);

    return () => {
      window.removeEventListener("suppliersLoading", handleLoadingEvent);
    };
  }, []);

  // Aplicar filtros cuando cambien
  useEffect(() => {
    applyFilters();
  }, [suppliers, searchTerm, filters]);

  // Manejar evento de carga
  const handleLoadingEvent = (event) => {
    setLoading(event.detail);
  };

  // Cargar proveedores
  const loadSuppliers = async () => {
    try {
      setLoading(true);
      const response = await getSuppliers(mantisBusiness._id);

      if (response.success) {
        setSuppliers(response.data);
        setFilteredSuppliers(response.data);
      } else {
        toast({
          title: "Error",
          description: "No se pudieron cargar los proveedores",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error al cargar proveedores:", error);
      toast({
        title: "Error",
        description: "Error al cargar los proveedores",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Add new supplier to the list
  const addNewSupplier = (newSupplier) => {
    setSuppliers((prevSuppliers) => [...prevSuppliers, newSupplier]);
    setFilteredSuppliers((prevFiltered) => [...prevFiltered, newSupplier]);
  };

  // Remove deleted supplier from the list
  const removeDeletedSupplier = (supplierId) => {
    setSuppliers((prevSuppliers) =>
      prevSuppliers.filter((s) => s.id !== supplierId)
    );
    setFilteredSuppliers((prevFiltered) =>
      prevFiltered.filter((s) => s.id !== supplierId)
    );
  };

  // Filtrar proveedores
  const applyFilters = () => {
    let filtered = [...suppliers];

    // Aplicar búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (supplier) =>
          supplier.name.toLowerCase().includes(term) ||
          supplier.contact_email.toLowerCase().includes(term) ||
          (supplier.description &&
            supplier.description.toLowerCase().includes(term))
      );
    }

    // Aplicar filtro de favoritos
    if (filters.favorite) {
      filtered = filtered.filter((supplier) => supplier.is_favorite);
    }

    // Aplicar filtro de país
    if (filters.country) {
      filtered = filtered.filter(
        (supplier) => supplier.country === filters.country
      );
    }

    // Aplicar filtro de calificación mínima
    if (filters.minRating > 0) {
      filtered = filtered.filter(
        (supplier) => supplier.rating_overall >= filters.minRating
      );
    }

    setFilteredSuppliers(filtered);
  };

  // Abrir modal para crear un nuevo proveedor
  const handleCreateSupplier = () => {
    setSelectedSupplier(null);
    setModalMode("create");
    setShowModal(true);
  };

  // Abrir modal para ver/editar un proveedor
  const handleSupplierClick = (supplier) => {
    setSelectedSupplier(supplier);
    setModalMode("view");
    setShowModal(true);
  };

  // Manejar cambio en los filtros
  const handleFilterChange = (newFilters) => {
    setFilters({...filters, ...newFilters});
  };

  // Cerrar modal
  const handleCloseModal = () => {
    setShowModal(false);
    // Recargar proveedores si se estaba en modo crear o editar
    if (modalMode !== "view") {
      loadSuppliers();
    }
  };

  // Cambiar a modo edición
  const handleEditMode = () => {
    setModalMode("edit");
  };

  return (
    <div className={`${embedded ? "p-0" : "p-6"} space-y-6`}>
      {/* Encabezado - Solo mostrar si no está embebido */}
      {!embedded && (
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
              Gestión de Proveedores
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Gestiona y mantén tus proveedores
            </p>
          </div>
          <Button
            onClick={handleCreateSupplier}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Proveedor
          </Button>
        </div>
      )}

      {/* Barra de filtros */}
      <SupplierFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filters={filters}
        onFilterChange={handleFilterChange}
        onRefresh={loadSuppliers}
        supplierCount={filteredSuppliers.length}
        totalCount={suppliers.length}
      />

      {/* Tabla de proveedores */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="w-full h-16 rounded" />
          ))}
        </div>
      ) : (
        <SuppliersTable
          suppliers={filteredSuppliers}
          onSupplierClick={handleSupplierClick}
          onSupplierCreated={addNewSupplier}
          onSupplierDeleted={removeDeletedSupplier}
          isLoading={loading}
          clientId={mantisBusiness._id}
        />
      )}

      {/* Modal de proveedor */}
      {showModal && (
        <SupplierModal
          supplier={selectedSupplier}
          mode={modalMode}
          onClose={handleCloseModal}
          onEditMode={handleEditMode}
          onSupplierCreated={addNewSupplier}
          onSupplierDeleted={removeDeletedSupplier}
          clientId={mantisBusiness._id}
        />
      )}
    </div>
  );
}

export default connect(selectAuthState)(SuppliersManagement)
